import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Star, Bell, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useTheme, ThemeToggle } from "../components/ThemeToggle";
import { SafetyActions } from "../components/SafetyActions";
import { RideMap } from "../components/RideMap";
import { ChatPanel } from "../components/ChatPanel";

export default function TrackRide() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [eta, setEta] = useState(null);
  const [hoverStar, setHoverStar] = useState(0);
  const [comment, setComment] = useState("");
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [alerts, setAlerts] = useState("idle"); // idle | on | denied | unsupported
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [reasons, setReasons] = useState([]);
  const [cancelling, setCancelling] = useState(false);
  const cancelled = data && data.status === "cancelled";
  const [theme, toggleTheme] = useTheme("night");

  useEffect(() => {
    api.get("/cancellation-reasons").then((r) => setReasons(r.data.client || [])).catch(() => {});
  }, []);

  const shareTrip = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Track my NamarckLimo ride", text: "Follow my chauffeur ride live:", url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Live trip link copied — share it with anyone.");
      }
    } catch { /* user dismissed the share sheet */ }
  };



  const cancelRide = async () => {
    if (!cancelReason) { toast.error("Please choose a reason"); return; }
    setCancelling(true);
    try {
      const { data: r } = await api.post(`/bookings/${data.booking_id}/cancel`, { reason: cancelReason, session_id: sessionId });
      setShowCancel(false);
      const feeMsg = r.cancellation_fee > 0 ? ` A ${r.cancellation_fee_pct}% cancellation fee ($${r.cancellation_fee.toFixed(2)}) may apply.` : "";
      toast.success(`Ride cancelled.${feeMsg}`);
      poll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not cancel");
    } finally {
      setCancelling(false);
    }
  };

  const urlB64ToUint8 = (b) => {
    const pad = "=".repeat((4 - (b.length % 4)) % 4);
    const base = (b + pad).replace(/-/g, "+").replace(/_/g, "/");
    const raw = atob(base);
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
  };

  const enableAlerts = async () => {
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) { setAlerts("unsupported"); return; }
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setAlerts("denied"); toast.error("Allow notifications to get arrival alerts."); return; }
      const { data: v } = await api.get("/push/vapid-public-key");
      if (!v.key) { toast.error("Alerts aren't set up yet."); return; }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8(v.key) });
      await api.post("/push/subscribe", { session_id: sessionId, subscription: sub.toJSON() });
      setAlerts("on");
      toast.success("Arrival alerts on — we'll notify you when your chauffeur is close.");
    } catch {
      toast.error("Couldn't enable alerts on this device.");
    }
  };


  const submitRating = async (stars) => {
    try {
      await api.post(`/track/${sessionId}/rating`, { stars, comment });
      setRatingSubmitted(true);
      toast.success("Thanks for rating your ride!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not submit rating");
    }
  };

  const poll = useCallback(async () => {
    try {
      const res = await api.get(`/track/${sessionId}`);
      setData(res.data);
    } catch {
      setError(true);
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) { setError(true); return; }
    poll();
    const t = setInterval(poll, 8000);
    return () => clearInterval(t);
  }, [poll, sessionId]);

  const steps = [
    { key: "reserved", label: "Reserved & confirmed", done: !!data },
    { key: "assigned", label: "Chauffeur assigned", done: data && data.driver_assigned },
    { key: "on_the_way", label: "Chauffeur on the way", done: data && ["on_the_way", "arrived", "in_progress", "completed"].includes(data.trip_status) },
    { key: "arrived", label: "Arrived at pickup", done: data && ["arrived", "in_progress", "completed"].includes(data.trip_status) },
    { key: "in_progress", label: "Ride in progress", done: data && ["in_progress", "completed"].includes(data.trip_status) },
    { key: "completed", label: "Ride completed", done: data && data.trip_status === "completed" },
  ];
  const activeIndex = steps.reduce((acc, s, i) => (s.done ? i : acc), -1);

  const hasLoc = data && ["on_the_way", "arrived", "in_progress"].includes(data.trip_status) && data.driver_lat != null && data.driver_lng != null;
  const hasPickup = data && data.pickup_lat != null && data.pickup_lng != null;
  const arriving = (eta != null && eta <= 5) || (data && data.arrival_alert);
  const notifiedRef = useRef(false);

  useEffect(() => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) { setAlerts("unsupported"); return; }
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((s) => { if (s) setAlerts("on"); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (arriving && !notifiedRef.current) {
      notifiedRef.current = true;
      if ("Notification" in window && Notification.permission === "granted") {
        try { new Notification("Your NamarckLimo chauffeur is arriving", { body: "Your driver is just minutes away — please be ready." }); } catch { /* ignore */ }
      }
    }
    if (!arriving) notifiedRef.current = false;
  }, [arriving]);

  const statusText = data ? (
    data.status === "cancelled" ? "Cancelled" :
    data.trip_status === "completed" ? "Completed" :
    data.trip_status === "in_progress" ? "On the way to drop-off" :
    data.trip_status === "arrived" ? "Arrived at pickup" :
    data.trip_status === "on_the_way" ? "On the way" :
    data.driver_assigned ? "Chauffeur assigned" : "Reserved"
  ) : "";

  const mapPhase = ["on_the_way", "arrived"].includes(data?.trip_status) ? "arriving" : "route";

  return (
    <div className="min-h-screen grain relative bg-[var(--nl-bg)]" data-theme={theme} data-testid="track-page">
      <ThemeToggle theme={theme} onToggle={toggleTheme} />

      {data && hasPickup && !cancelled && (
        <div className="w-full" data-testid="track-hero-map">
          <RideMap
            theme={theme}
            phase={mapPhase}
            pickup={{ lat: data.pickup_lat, lng: data.pickup_lng, name: data.pickup }}
            dropoff={{ lat: data.dropoff_lat, lng: data.dropoff_lng, name: data.dropoff }}
            car={hasLoc ? { lat: data.driver_lat, lng: data.driver_lng } : null}
            onEta={setEta}
            className="h-[52vh] md:h-[58vh]"
            info={{
              rideName: data.vehicle,
              driverName: data.driver_assigned ? data.driver_name : "",
              plate: [data.car_model, data.plate].filter(Boolean).join(" · "),
              photo: data.driver_photo,
              statusText,
            }}
          />
        </div>
      )}

      <div className="flex items-start justify-center px-6 py-10">
      <motion.div
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-lg border border-white/10 bg-[var(--nl-bg-2)] p-8 md:p-10"
      >
        <Link to="/" className="flex items-baseline gap-1 mb-2 justify-center">
          <span className="font-serif text-2xl">Namarck</span>
          <span className="font-serif text-2xl italic text-gold">Limo</span>
        </Link>
        <p className="label text-gold text-center mb-8">Track Your Ride</p>

        {error && <p className="text-white/50 font-body text-center py-8" data-testid="track-error">We couldn't find this ride. Check your link or contact admin@namarcklimo.ca.</p>}

        {data && (
          <>
            <div className="border border-white/10 divide-y divide-white/5 mb-8">
              {[["When", `${data.date} · ${data.time}`], ["Vehicle", data.vehicle], ["Pickup", data.pickup], ["Drop-off", data.dropoff]].map(([k, v]) => (
                <div key={k} className="flex justify-between px-5 py-3 font-body text-sm">
                  <span className="text-white/40">{k}</span><span className="text-white/80 text-right">{v}</span>
                </div>
              ))}
            </div>

            {data.driver_assigned ? (
              <div className="border border-gold/25 bg-[var(--nl-bg-3)] p-5 mb-8 flex items-center gap-4" data-testid="trip-card">
                {data.driver_photo ? (
                  <img src={data.driver_photo} alt={data.driver_name} className="w-16 h-16 rounded-full object-cover border-2 border-gold shrink-0" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gold/15 border-2 border-gold flex items-center justify-center font-serif text-2xl text-gold shrink-0">
                    {(data.driver_name || "?").charAt(0)}
                  </div>
                )}
                <div className="min-w-0">
                  <p className="label text-gold">Your chauffeur</p>
                  <p className="font-serif text-xl truncate">{data.driver_name}</p>
                  <p className="text-white/50 font-body text-sm truncate">
                    {[data.car_model, data.plate].filter(Boolean).join(" · ") || data.vehicle}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-white/40 font-body text-sm mb-8" data-testid="chauffeur-pending">Chauffeur assigning soon…</p>
            )}

            {cancelled && (
              <div className="border border-red-500/40 bg-red-500/10 p-5 mb-8 text-center" data-testid="track-cancelled">
                <p className="label text-red-400 mb-1">Ride Cancelled</p>
                <p className="font-serif text-xl text-white">Cancelled by {data.cancelled_by === "client" ? "you" : data.cancelled_by === "driver" ? "your chauffeur" : "our team"}</p>
                {data.cancellation_reason && <p className="text-white/50 font-body text-sm mt-1">Reason: {data.cancellation_reason}</p>}
                {data.cancellation_fee > 0 && <p className="text-white/40 font-body text-xs mt-2">Cancellation fee: {data.cancellation_fee_pct}% (${data.cancellation_fee.toFixed(2)})</p>}
                <p className="text-white/40 font-body text-xs mt-3">Need a ride? <Link to="/#book" className="text-gold">Book again</Link> or call +1 289 928 2427.</p>
              </div>
            )}

            {!cancelled && data.trip_status !== "completed" && data.driver_assigned && (
              <div className="mb-6" data-testid="rider-chat">
                <ChatPanel
                  me="rider"
                  title={`Chat with ${data.driver_name || "your chauffeur"}`}
                  listUrl={`/track/${sessionId}/chat`}
                  sendUrl={`/track/${sessionId}/chat`}
                  quickReplies={["I'm ready", "I'm outside", "Give me 5 min", "Thank you!"]}
                  testPrefix="rider-chat"
                />
              </div>
            )}

            {!cancelled && data.trip_status !== "completed" && (
              <button
                onClick={shareTrip}
                data-testid="share-trip-btn"
                className="w-full mb-6 label text-gold border border-gold/40 py-3 flex items-center justify-center gap-2 hover:bg-gold/10 transition-colors duration-300"
              >
                <Share2 size={15} /> Share live trip
              </button>
            )}

            <div className="space-y-1" data-testid="track-timeline">
              {steps.map((s, i) => (
                <div key={s.key} className="flex items-start gap-4" data-testid={`track-step-${s.key}`}>
                  <div className="flex flex-col items-center">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center border transition-colors duration-500 ${s.done ? "bg-gold border-gold text-black" : i === activeIndex + 1 ? "border-gold text-gold animate-pulse" : "border-white/20 text-white/30"}`}>
                      {s.done ? <Check size={16} /> : <span className="text-xs">{i + 1}</span>}
                    </div>
                    {i < steps.length - 1 && <div className={`w-px h-8 ${s.done ? "bg-gold" : "bg-white/10"}`} />}
                  </div>
                  <p className={`font-body pt-1.5 ${s.done ? "text-white" : "text-white/40"}`}>{s.label}</p>
                </div>
              ))}
            </div>

            {data.trip_status !== "completed" && alerts !== "unsupported" && (
              <div className="mt-6" data-testid="push-alerts">
                {alerts === "on" ? (
                  <p className="flex items-center gap-2 label text-emerald-400 text-xs" data-testid="alerts-on">
                    <span className="w-2 h-2 rounded-full bg-emerald-400" /> Arrival alerts on
                  </p>
                ) : (
                  <button
                    onClick={enableAlerts}
                    data-testid="enable-alerts-btn"
                    className="w-full flex items-center justify-center gap-2 label text-gold border border-gold/40 py-3 hover:bg-gold/10 transition-colors duration-300"
                  >
                    <Bell size={15} /> Notify me when my chauffeur arrives
                  </button>
                )}
              </div>
            )}

            {!cancelled && data.trip_status !== "completed" && (
              <button
                onClick={() => setShowCancel(true)}
                data-testid="open-cancel-btn"
                className="mt-4 w-full label text-red-400/80 border border-red-400/30 py-3 hover:bg-red-400/10 transition-colors duration-300"
              >
                Cancel this ride
              </button>
            )}

            {data.booking_id && (
              <SafetyActions idPayload={{ role: "rider", session_id: sessionId }} reportKind="lost" testPrefix="track" />
            )}

            {arriving && !cancelled && (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
                className="mb-6 border border-gold bg-gold/10 px-5 py-4 flex items-center gap-3"
                data-testid="arrival-alert"
              >
                <span className="w-2.5 h-2.5 rounded-full bg-gold animate-pulse shrink-0" />
                <p className="font-body text-sm text-white">Your chauffeur is <span className="text-gold font-semibold">arriving now</span> — please be ready at the pickup.</p>
              </motion.div>
            )}

            {data.trip_status === "completed" && (
              <div className="mt-8 border-t border-white/10 pt-6" data-testid="rating-section">
                {(data.rating || ratingSubmitted) ? (
                  <div className="text-center" data-testid="rating-done">
                    <p className="label text-gold mb-3">Thanks for riding with us</p>
                    <div className="flex justify-center gap-1">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star key={i} size={20} className={i <= (data.rating || 0) ? "fill-gold text-gold" : "text-white/20"} />
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="label text-gold text-center mb-3">How was your ride?</p>
                    <textarea
                      value={comment} onChange={(e) => setComment(e.target.value)} rows={2}
                      placeholder="Tell us more (optional)"
                      className="w-full bg-[var(--nl-bg-3)] border border-white/10 px-4 py-3 text-white font-body text-sm mb-4 focus:outline-none focus:border-gold transition-colors duration-300"
                      data-testid="rating-comment"
                    />
                    <div className="flex justify-center gap-2">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <button
                          key={i} type="button"
                          onMouseEnter={() => setHoverStar(i)} onMouseLeave={() => setHoverStar(0)}
                          onClick={() => submitRating(i)}
                          className="transition-transform duration-200 hover:scale-110"
                          data-testid={`rate-star-${i}`}
                        >
                          <Star size={32} className={i <= hoverStar ? "fill-gold text-gold" : "text-white/25"} />
                        </button>
                      ))}
                    </div>
                    <p className="text-white/30 text-xs font-body text-center mt-3">Tap a star to submit</p>
                  </>
                )}
              </div>
            )}

            <p className="text-white/30 text-xs font-body text-center mt-8">This page updates automatically. Questions? admin@namarcklimo.ca</p>
          </>
        )}

        <Link to="/" data-testid="track-home-btn" className="mt-8 block text-center label text-white/50 hover:text-gold transition-colors duration-300">← Back to Home</Link>
      </motion.div>
      </div>

      {showCancel && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowCancel(false)} data-testid="cancel-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md border border-white/10 bg-[var(--nl-bg-2,#111)] p-8 space-y-5">
            <div>
              <p className="label text-red-400 mb-1">Cancel Ride</p>
              <h3 className="font-serif font-light text-2xl text-white">Why are you cancelling?</h3>
              <p className="text-white/40 text-xs font-body mt-2">A fee may apply based on how close it is to your pickup time (10% early, 75% within 40 min, 100% no-show).</p>
            </div>
            <select value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} className="w-full bg-[var(--nl-bg-3,#1a1a1a)] border border-white/10 px-4 py-2.5 text-white font-body text-sm focus:outline-none focus:border-gold" data-testid="cancel-reason-select">
              <option value="">Select a reason…</option>
              {reasons.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <div className="flex gap-3">
              <button onClick={() => setShowCancel(false)} className="flex-1 label border border-white/15 text-white/60 py-3 hover:text-white transition-colors duration-300" data-testid="cancel-dismiss">Keep ride</button>
              <button onClick={cancelRide} disabled={cancelling} className="flex-1 label bg-red-500 text-white py-3 hover:bg-red-600 transition-colors duration-300 disabled:opacity-50" data-testid="cancel-confirm">{cancelling ? "Cancelling…" : "Cancel ride"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
